import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import TableDocuments from '../ClinicComponent/TableDocuments';
import Swal from 'sweetalert2';
import CompleteProfilePatient from './CompletProfilePatient';
import ImageCropper from '../ImageCropper/ImageCropper';
import apiClient from '../../utils/axiosPetition';
import RedesSociales from './RedesSociales';
import { alertaGeneral } from '../../utils/alertas';
import Cargando from '../../utils/Cargando';
import UserProfile from '../AdminComponent/UserProfile';
import { validarInputExpresionRegular } from '../../utils/generalFunctions';
import { useDataUser } from '../../context/dataUserContext';
import CompleteDocuments from './CompleteDocuments';

const PatientProfile = () => {
    const { patientId, role, doctorId } = useDataUser();

    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [file, setFile] = useState([]);
    const [isEdit, setIsEdit] = useState(false);
    const [inputsPatient, setInputsPatient] = useState(null);
    const [showGuardarCambios, setShowGuardarCambios] = useState(false);
    const [originalFields, setOriginalFields] = useState({});
    const [showImageCropper, setShowImageCropper] = useState(false);
    const [fileToCrop, setFileToCrop] = useState(null);
    const [previewPhotoUrl, setPreviewPhotoUrl] = useState(null);

    // Vista previa de la foto recién recortada (y liberar object URL al desmontar o cambiar)
    useEffect(() => {
        if (file?.foto_perfil instanceof File) {
            const url = URL.createObjectURL(file.foto_perfil);
            setPreviewPhotoUrl(url);
            return () => URL.revokeObjectURL(url);
        }
        setPreviewPhotoUrl(null);
    }, [file?.foto_perfil]);

    const setFilesToEditWithCropper = useCallback((newFiles) => {
        if (newFiles?.foto_perfil && newFiles.foto_perfil instanceof File && newFiles.foto_perfil.type.startsWith('image/')) {
            setFileToCrop(newFiles.foto_perfil);
            setShowImageCropper(true);
        } else {
            setFile(newFiles);
        }
    }, []);

    const handleCropComplete = useCallback((croppedFile) => {
        setFile(prev => ({ ...(prev || {}), foto_perfil: croppedFile }));
        setShowImageCropper(false);
        setFileToCrop(null);
    }, []);

    const handleCropCancel = useCallback(() => {
        setShowImageCropper(false);
        setFileToCrop(null);
    }, []);

    useEffect(() => {
        const fetchPatientProfile = async () => {
            try {
                // Si es doctor, siempre usar la lista de pacientes que devuelve solo tu perfil
                if (role === 'doctor') {
                    try {
                        const response = await apiClient.get('/api/patient/');
                        const patients = response.data;
                        
                        if (patients && patients.length > 0) {
                            // El endpoint devuelve un array con solo el perfil del doctor
                            const doctorPatient = patients[0];
                            setPatient(doctorPatient);
                            setLoading(false);
                            setOriginalFields({
                                cellNumber: doctorPatient.cellNumber,
                                email: doctorPatient.email
                            });
                        } else {
                            // No hay perfil de paciente, crear objeto inicial para el formulario
                            setPatient({
                                _id: null,
                                urlPictureProfile: '',
                                urlFileDocument: '',
                                profileComplete: false,
                                firstName: '',
                                secondName: '',
                                firstLastName: '',
                                secondLastName: '',
                                email: '',
                                cellNumber: '',
                                idType: '',
                                idNumber: ''
                            });
                            setLoading(false);
                        }
                    } catch (err) {
                        console.error('Error obteniendo perfil de paciente del doctor:', err);
                        // Crear objeto paciente inicial para que se pueda completar el formulario
                        setPatient({
                            _id: null,
                            urlPictureProfile: '',
                            urlFileDocument: '',
                            profileComplete: false,
                            firstName: '',
                            secondName: '',
                            firstLastName: '',
                            secondLastName: '',
                            email: '',
                            cellNumber: '',
                            idType: '',
                            idNumber: ''
                        });
                        setLoading(false);
                    }
                    return;
                }

                // Para pacientes regulares, usar patientId del contexto
                if (patientId) {
                    try {
                        const response = await apiClient.get(`/api/patient/${patientId}`);
                        setPatient(response.data);
                        setLoading(false);
                        setOriginalFields({
                            cellNumber: response.data.cellNumber,
                            email: response.data.email
                        });
                    } catch (err) {
                        console.error('Error obteniendo perfil de paciente:', err);
                        setError('Error al obtener los datos del paciente');
                        setLoading(false);
                    }
                } else {
                    setError('No se pudo determinar el paciente');
                    setLoading(false);
                }
            } catch (err) {
                console.error('Error en fetchPatientProfile:', err);
                setError('Error al obtener los datos del paciente');
                setLoading(false);
            }
        };

        if (patientId || role === 'doctor') {
            fetchPatientProfile();
        } else {
            setError('No se pudo determinar el paciente');
            setLoading(false);
        }
    }, [patientId, role, doctorId]);

    useEffect(() => {
        if (!isEdit) {
            setPatient({
                ...patient,
                cellNumber: originalFields.cellNumber,
                email: originalFields.email
            })
        }
    }, [isEdit])

    useEffect(() => {
        if (patient) {
            setInputsPatient([
                {
                    label: 'Nombres Completos',
                    type: 'text',
                    value: `${patient?.firstName} ${patient?.secondName || ''} ${patient?.firstLastName} ${patient?.secondLastName || ''}`,
                    readOnly: true
                },
                {
                    label: 'Género',
                    type: 'select',
                    value: patient.gender || 'male',
                    readOnly: !isEdit,
                    options: [
                        { value: 'male', label: 'Masculino' },
                        { value: 'female', label: 'Femenino' }
                    ],
                    onChange: (ev) => { setPatient({ ...patient, gender: ev.target.value }) }
                },
                {
                    label: 'Fecha de Nacimiento',
                    type: 'text',
                    value: new Date(patient.dob).toLocaleDateString(),
                    readOnly: true
                },
                {
                    label: 'País de Nacimiento',
                    type: 'text',
                    value: patient.birthCountry,
                    readOnly: true
                },
                {
                    label: 'País de Residencia',
                    type: 'text',
                    value: patient.residenceCountry,
                    readOnly: true
                },
                {
                    label: 'Municipio / Ciudad',
                    type: 'text',
                    value: patient.municipio,
                    readOnly: true
                },
                {
                    label: 'Barrio',
                    type: 'text',
                    value: patient.neighborhood,
                    readOnly: true
                },
                {
                    label: 'Número de Celular',
                    type: 'text',
                    value: patient.cellNumber,
                    readOnly: !isEdit,
                    onChange: (ev) => { setPatient({ ...patient, cellNumber: validarInputExpresionRegular(ev, /^[0-9]+$/g) }) }
                },
                {
                    label: 'Correo Electrónico',
                    type: 'email',
                    value: patient.email,
                    readOnly: (role === 'doctor' ? true : !isEdit),
                    onChange: (ev) => { setPatient({ ...patient, email: ev.target.value }) }
                },
                {
                    label: 'Tipo de Identificación',
                    type: 'text',
                    value: patient.idType?.toUpperCase(),
                    readOnly: true
                },
                {
                    label: 'Número de Identificación',
                    type: 'text',
                    value: patient.idNumber,
                    readOnly: true
                },
            ])
        }
    }, [patient, isEdit]);

    useEffect(() => {
        if (file.foto_perfil) {
            setShowGuardarCambios(true);
        } else {
            setShowGuardarCambios(false);
        }
    }, [file]);

    useEffect(() => {
        if (localStorage.getItem('showTeleconsultasModal') === 'true') {
            console.log('Bandera detectada, mostrando modal en 2 segundos...');
            setTimeout(() => {
                alertaGeneral({
                    titulo: '¡Perfil Completado!',
                    messageHtml: 'Gracias por completar tu perfil! Tus datos han sido guardados exitosamente. Ahora puede acceder a sus teleconsultas agendadas.',
                    isError: false,
                    showConfirmButton: true,
                    confirmButtonText: 'Ir a mis teleconsultas',
                    confirmButtonColor: '#3085d6',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    allowEnterKey: false,
                    showCancelButton: false
                }).then((result) => {
                    if (result.isConfirmed) {
                        window.location.href = '/patient/teleconsultas';
                    }
                });
                localStorage.removeItem('showTeleconsultasModal');
            }, 2000);
        } else {
            console.log('Bandera NO detectada, no se mostrará el modal.');
        }
    }, []);

    const documentsExists = [
        { nameToSave: 'foto_perfil', name: 'Foto Perfil', url: patient?.urlPictureProfile || '', isEdit: true },
        { nameToSave: 'cedulaPDF', name: 'Documento Identidad', url: patient?.urlFileDocument || '', isEdit: false },
    ];

    const onHandleSubmit = async (photoFileOverride) => {
        const fotoFile = (photoFileOverride && photoFileOverride instanceof File)
            ? photoFileOverride
            : (file?.foto_perfil instanceof File ? file.foto_perfil : null);
        if (!fotoFile) {
            alertaGeneral({ titulo: 'Sin foto', messageHtml: 'No hay una nueva foto para guardar.', isError: true });
            return;
        }
        const dataPatientToSend = new FormData();
        dataPatientToSend.append('idNumber', patient.idNumber);
        dataPatientToSend.append('clinicInfo', patient.clinicInfo._id);
        dataPatientToSend.append('foto_perfil', fotoFile);

        try {
            const response = await apiClient.put(`/api/patient/updatePictureProfile/${patient?._id}`, dataPatientToSend);

            if (response.status === 200) {
                const newImageUrl = response.data;
                localStorage.setItem('imageProfile', newImageUrl);
                window.dispatchEvent(new CustomEvent('imageProfileUpdated', { detail: newImageUrl }));
                Swal.fire({
                    icon: 'success',
                    title: 'Foto actualizada correctamente',
                }).then(() => {
                    window.location.reload();
                });
            }
        } catch (err) {
            alertaGeneral({
                titulo: 'Error',
                messageHtml: 'Error al actualizar la foto, por favor intenta de nuevo.',
                isError: true
            });
            console.log(err);
        }
    }

    if (loading) return (
        <Cargando message={"Cargando perfil..."} />
    );

    if (error && !patient) return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-center text-red-600">{error}</p>
        </div>
    );

    // Si no hay paciente o el perfil no está completo, mostrar formulario de completar
    if (!patient || !patient.profileComplete) {
        return <CompleteProfilePatient patient={patient || {}} />;
    }

    return (
        <div className="space-y-9">
            <UserProfile
                tipoPerfil={'Perfil del usuario'}
                userImage={previewPhotoUrl || patient.urlPictureProfile}
                inputs={inputsPatient}
                onEdit={() => setIsEdit(!isEdit)}
                onCancelar={() => setIsEdit(false)}
                onGuardar={onGuardar}
                onPhotoChange={(croppedFile) => {
                    if (croppedFile) setFile(prev => ({ ...(prev || {}), foto_perfil: croppedFile }));
                }}
                onPhotoAcceptAndSave={role !== 'doctor' ? (croppedFile) => {
                    if (croppedFile) setFile(prev => ({ ...(prev || {}), foto_perfil: croppedFile }));
                    onHandleSubmit(croppedFile);
                } : undefined}
            />

            {/* Botón para guardar la foto cuando el paciente acaba de recortarla (visible y a mano) */}
            {role !== 'doctor' && file?.foto_perfil instanceof File && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-between gap-4">
                    <p className="text-sm text-gray-600">
                        <strong>Nueva foto de perfil lista.</strong> Guarda los cambios para actualizarla.
                    </p>
                    <button
                        type="button"
                        onClick={onHandleSubmit}
                        className="flex-shrink-0 px-4 py-2 bg-[#1f94c2ff] hover:bg-[#1a7ba8] text-white font-semibold rounded-lg transition-colors"
                    >
                        Guardar foto
                    </button>
                </div>
            )}

            {(patient.documentComplete || patient.urlFileDocument) && role !== 'doctor' ? (

                <TableDocuments
                    lstDocuments={documentsExists ? documentsExists : []}
                    filesToEdit={file}
                    setFilesToEdit={setFilesToEditWithCropper}
                    showGuardarCambios={showGuardarCambios}
                    onClickGuardarCambios={onHandleSubmit}
                />
            ) : (
                role !== 'doctor' && <CompleteDocuments/>
            )}

            <RedesSociales
                redesSociales={patient.socialMedia}
                isPatient={true}
                userId={patient._id}
            />

            {showImageCropper && fileToCrop && typeof document !== 'undefined' && createPortal(
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2147483647, backgroundColor: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ImageCropper
                        imageFile={fileToCrop}
                        onCropComplete={handleCropComplete}
                        onCropCancel={handleCropCancel}
                    />
                </div>,
                document.body
            )}
        </div >
    );

    function onGuardar() {
        console.log(patient);
        
        // Preparar datos a enviar
        const dataToUpdate = {
            gender: patient.gender,
            cellNumber: patient.cellNumber,
            email: patient.email
        };

        // Enviar actualización al backend (endpoint correcto)
        apiClient.put(`/api/patient/editProfile/${patientId}`, dataToUpdate)
            .then((res) => {
                alertaGeneral({
                    titulo: 'Éxito',
                    messageHtml: 'Los cambios se han guardado correctamente.',
                    isError: false
                }).then(() => {
                    setIsEdit(false);
                    // Recargar datos del paciente
                    window.location.reload();
                });
            })
            .catch((err) => {
                alertaGeneral({
                    titulo: 'Error',
                    messageHtml: err.response?.data?.message || 'Error al guardar los cambios.',
                    isError: true
                });
                console.error('Error al guardar cambios:', err);
            });
    }

};

export default PatientProfile;